import dictionaryUrl from './pt-br-words.bin?url'
import type { SpellRequest, SpellResponse } from './spell.worker'
import type { Range } from './tokens'

/*
 * Ponte com o worker do corretor. Sobe na primeira vez que alguém foca um
 * texto livre; se o navegador não tiver Worker/DecompressionStream, o corretor
 * simplesmente não aparece (nunca quebra o campo).
 */

const EXTRA_KEY = 'supply:spell:extra'

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void }
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never

let worker: Worker | null = null
let ready: Promise<void> | null = null
let seq = 0
const pending = new Map<number, Pending>()

export const spellSupported = () => typeof Worker !== 'undefined' && typeof DecompressionStream !== 'undefined'

function readExtra(): string[] {
  try {
    const raw = localStorage.getItem(EXTRA_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function send<T>(msg: DistributiveOmit<SpellRequest, 'id'>): Promise<T> {
  if (!worker) return Promise.reject(new Error('corretor desligado'))
  const id = ++seq
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
    worker!.postMessage({ ...msg, id })
  })
}

/** Liga o corretor (uma vez por aba). */
export function startSpell(): Promise<void> {
  if (ready) return ready
  if (!spellSupported()) return (ready = Promise.reject(new Error('sem suporte')))
  worker = new Worker(new URL('./spell.worker.ts', import.meta.url), { type: 'module' })
  worker.onmessage = (e: MessageEvent<SpellResponse>) => {
    const p = pending.get(e.data.id)
    if (!p) return
    pending.delete(e.data.id)
    if (e.data.ok) p.resolve(e.data.result)
    else p.reject(new Error(e.data.error))
  }
  ready = send<void>({ type: 'init', url: new URL(dictionaryUrl, location.href).href, extra: readExtra() })
  ready.catch(() => undefined)
  return ready
}

export async function checkText(text: string, caret: number): Promise<Range[]> {
  await startSpell()
  return send<Range[]>({ type: 'check', text, caret })
}

export async function suggestWord(word: string): Promise<string[]> {
  await startSpell()
  return send<string[]>({ type: 'suggest', word })
}

/** "Adicionar ao dicionário": vale para esta pessoa, neste navegador. */
export async function addToDictionary(word: string): Promise<void> {
  const extra = readExtra()
  if (!extra.includes(word.toLocaleLowerCase('pt-BR'))) extra.push(word.toLocaleLowerCase('pt-BR'))
  try {
    localStorage.setItem(EXTRA_KEY, JSON.stringify(extra))
  } catch {
    // Sem armazenamento (aba anônima): vale só até fechar a aba.
  }
  await startSpell()
  await send<void>({ type: 'add', words: [word] })
}
